import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, useCallback, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import mermaid from 'mermaid';
import ExcalidrawComponent from './ExcalidrawComponent';
import { extractBlock, isSpecialBlock, processMarkdownIndentation, isTableBlock, isExcalidrawBlock, isMermaidBlock, isListItemBlock } from '../utils/EditorUtils';
import { EditorContainer, BlockContainer, ListContainer, MarkdownTable } from '../config/EditorStyling';
import LineBlock from './editor/lineblock';
import TableBlock, { parseTableContent } from './editor/tableblock';
import ExcalidrawBlock, { extractExcalidrawRef, createExcalidrawMarkdown } from './editor/excalidrawblock';
import MermaidBlock, { extractMermaidContent, createMermaidMarkdown } from './editor/mermaidblock';
import ListItemBlock, { markdownRenderers } from './editor/listitemblock';

// Function to extract block content including end markers


// Function to save and restore cursor position



// Helper function to find the line index from a DOM node (ensure it's robust)
const getLineIndexFromNode = (node, lineRefs) => {
  if (!node || !lineRefs.current) {
    console.warn('[getLineIndexFromNode] Null node or lineRefs.current is null/empty');
    return null;
  }
  if (lineRefs.current.length === 0) {
    console.warn('[getLineIndexFromNode] lineRefs.current is empty');
    return null;
  }
  
  //console.log('[getLineIndexFromNode] Searching for node:', node, 
             
  // //console.log('[getLineIndexFromNode] lineRefs.current sample (first 5):', lineRefs.current.slice(0, 5).map(r => r ? {tagName: r.tagName, id: r.id, classes: r.classList ? Array.from(r.classList).join(' ') : ''} : null ));

  let currentNode = node;
  let depth = 0;
  while (currentNode && depth < 15) { // Increased depth limit slightly
    // //console.log(`[getLineIndexFromNode] Depth ${depth}: Checking currentNode:`, currentNode.tagName, currentNode.id, currentNode.classList ? Array.from(currentNode.classList).join(' ') : '');
    
    const directIndex = lineRefs.current.findIndex(ref => ref === currentNode);
    if (directIndex !== -1) {
      //console.log(`[getLineIndexFromNode] Found direct match at index: ${directIndex} for node:`, currentNode.tagName, currentNode.id);
      return directIndex;
    }
    
    if (currentNode.parentElement) {
        const parentIndex = lineRefs.current.findIndex(ref => ref === currentNode.parentElement);
        if (parentIndex !== -1) {
          //console.log(`[getLineIndexFromNode] Found parent match at index: ${parentIndex} for parent:`, currentNode.parentElement.tagName, currentNode.parentElement.id);
          return parentIndex;
        }
    }
    
    if (currentNode.classList && currentNode.classList.contains('EditorContainer')) {
        //console.log('[getLineIndexFromNode] Reached EditorContainer (currentNode is EditorContainer), stopping search.');
        break;
    }
    if (currentNode.parentElement && currentNode.parentElement.classList && currentNode.parentElement.classList.contains('EditorContainer') && depth > 0) {
        // This means currentNode is a direct child of EditorContainer and wasn't matched.
        //console.log('[getLineIndexFromNode] CurrentNode is direct child of EditorContainer and not matched, stopping search.');
        break;
    }

    currentNode = currentNode.parentElement;
    depth++;
  }
  console.warn('[getLineIndexFromNode] Node not found in lineRefs after traversing up the DOM. Depth reached:', depth);
  return null;
};



// Render special blocks like unknown types
const RenderUnknownBlock = ({ content }) => {

  return (
    <div className="unknown-block-container">
      <pre>{content.join('\n')}</pre>
    </div>
  );
};

// Replace the previous complex components with a simpler approach
// markdownRenderers is now imported from ListItemBlock component

const ObsidianEditor = forwardRef(({ value, onChange, onSave }, ref) => {
  const [lines, setLines] = useState(['']);
  const [activeLineIndex, setActiveLineIndex] = useState(null);
  const [isComposing, setIsComposing] = useState(false);
  const [activeBlock, setActiveBlock] = useState(null);
  const [editingBlock, setEditingBlock] = useState(false);
  const lineRefs = useRef([]);
  const containerRef = useRef(null);
  const prevValueRef = useRef('');
  const prevLinesRef = useRef(['']);
  const debouncedUpdateRef = useRef(null);
  const blockEditTimerRef = useRef(null);
  const activeLineContentRef = useRef(''); // Track current content of the active line
  const skipNextBlurCommit = useRef(false); // Flag to skip commit on blur after certain actions
  const blockIdMapRef = useRef(new Map()); // Add definition back

  // Initialize mermaid once when component mounts
  useEffect(() => {
    if (typeof mermaid !== 'undefined' && !mermaid.initialized) {
      mermaid.initialize({
        startOnLoad: true,
        theme: 'default',
        securityLevel: 'loose',
      });
    }
  }, []);
  
  // ***** START: New useEffect for logging clicks *****
  useEffect(() => {
    const editorContainer = containerRef.current;
    if (!editorContainer) return;

    const handleClickLogger = (event) => {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
      } else {
        console.log('Clicked target:', event.target, '- No selection on click.');
      }
    };

    editorContainer.addEventListener('click', handleClickLogger);
    return () => {
      editorContainer.removeEventListener('click', handleClickLogger);
    };
  }, [activeLineIndex]); // Re-bind if activeLineIndex changes, though lineRefs stability is more key here
  // ***** END: New useEffect for logging clicks *****
  
  // Process content into blocks including special handling for mermaid/excalidraw
  const processedContent = useMemo(() => {
    const result = [];
    let i = 0;
    
    while (i < lines.length) {
      if (isSpecialBlock(lines[i])) {
        const { content, endIndex } = extractBlock(lines, i);
        result.push({
          type: 'block',
          content,
          startIndex: i,
          endIndex
        });
        i = endIndex + 1;
      } else {
        result.push({
          type: 'line',
          content: lines[i],
          index: i
        });
        i++;
      }
    }
    
    return result;
  }, [lines]);
  
  // Handle initial content and updates from parent
  useEffect(() => {
    if (value !== prevValueRef.current) {
      prevValueRef.current = value;
      if (value && typeof value === 'string') {
        const initialLines = value.split('\n');
        setLines(initialLines);
        prevLinesRef.current = initialLines;
      }
    }
  }, [value]);
  
  // Debounced change handler
  const debouncedChange = useCallback((newLines) => {
    if (debouncedUpdateRef.current) {
      clearTimeout(debouncedUpdateRef.current);
    }
    
    debouncedUpdateRef.current = setTimeout(() => {
    if (!isComposing && onChange) {
      const newContent = newLines.join('\n');
      if (newContent !== prevValueRef.current) {
        prevValueRef.current = newContent;
        onChange(newContent);
      }
    }
    }, 50); // 50ms debounce to reduce jitter
  }, [onChange, isComposing]);
  
  // Propagate changes to parent
  const notifyChange = useCallback((newLines) => {
    prevLinesRef.current = newLines;
    debouncedChange(newLines);
  }, [debouncedChange]);
  
  // Update the commitActiveLine function to work with LineBlock's own content management
  const commitActiveLine = useCallback((lineContent) => {
    if (activeLineIndex !== null) {
      const newLines = [...prevLinesRef.current];
      
      // If line content is directly provided, use it
      if (lineContent !== undefined) {
        newLines[activeLineIndex] = lineContent;
        setLines(newLines);
        prevLinesRef.current = newLines;
        notifyChange(newLines);
        return true;
      }
      
      // Otherwise, use the old approach for backward compatibility
      if (lineRefs.current[activeLineIndex]) {
        const currentDOMContent = lineRefs.current[activeLineIndex].innerText;
        const previousStateContent = prevLinesRef.current[activeLineIndex];
        
        if (currentDOMContent !== previousStateContent) {
          newLines[activeLineIndex] = currentDOMContent;
          setLines(newLines);
          prevLinesRef.current = newLines;
          notifyChange(newLines);
          return true;
        }
      }
    }
    return false;
  }, [activeLineIndex, notifyChange]);
  
  // Handle content change from a specific line
  const handleLineContentChange = useCallback((lineIndex, content) => {
    // Store the content temporarily, but don't commit it until needed
    activeLineContentRef.current = content;
  }, []);
  
  // Handle inserting markdown at a specific line
  const handleInsertMarkdown = useCallback((markdown, index) => {
    const newLines = [...prevLinesRef.current];
    const currentLine = newLines[index] || '';
    
    if (currentLine.trim()) {
      newLines[index] = currentLine + '\n' + markdown;
    } else {
      newLines[index] = markdown;
    }
    
    setLines(newLines);
    prevLinesRef.current = newLines;
    notifyChange(newLines);
    
    // Focus on the line where we inserted
    setTimeout(() => {
      setActiveLineIndex(index);
      if (lineRefs.current[index]) {
        lineRefs.current[index].focus();
        
        // Place cursor at end of line
        const selection = window.getSelection();
        const range = document.createRange();
        const el = lineRefs.current[index];
        
        if (el.lastChild) {
          range.setStartAfter(el.lastChild);
        } else {
          range.setStart(el, 0);
        }
        
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }, 10);
  }, [notifyChange]);
  
  // Add a method to find the line where the cursor is currently positioned
  const getCurrentLineFromCursor = useCallback(() => {
    // Get the current selection
    const selection = window.getSelection();
    if (!selection || !selection.rangeCount) {
      //console.log('No selection found');
      return null;
    }
    
    const range = selection.getRangeAt(0);
    //console.log('Selection range:', range.startContainer, range.startOffset);
    
    // Find which line contains the current cursor position
    const cursorLineIdx = getLineIndexFromNode(range.startContainer, lineRefs);
    //console.log('Cursor line index:', cursorLineIdx, 'Active line index:', activeLineIndex);
    
    return cursorLineIdx !== null ? cursorLineIdx : activeLineIndex;
  }, [activeLineIndex]);
  
  // Add method for inserting content at cursor split point
  const insertAtCursorPosition = useCallback((content) => {
    //console.log('=== insertAtCursorPosition START ===');
    //console.log('Input content:', content);
    //console.log('Current activeLineIndex when insertion starts:', activeLineIndex);
    
    // First try to get a valid line index from selection
    const selection = window.getSelection();
    let determinedLineIndex = null;
    
    if (selection && selection.rangeCount) {
      const range = selection.getRangeAt(0).cloneRange();
      
      
      determinedLineIndex = getLineIndexFromNode(range.startContainer, lineRefs);
      //console.log('Line index from node (using original range startContainer):', determinedLineIndex);
    }
    
    // Double-check that we have a valid line index - either from activeLineIndex or from selection
    const targetLineIndex = (activeLineIndex !== null && activeLineIndex >= 0) ? 
      activeLineIndex : determinedLineIndex;
    
    // Now use the determined line index if it's valid
    if (targetLineIndex !== null && targetLineIndex >= 0 && targetLineIndex < prevLinesRef.current.length) {

      
      // Get content for the target line
      const currentLineContent = prevLinesRef.current[targetLineIndex] || '';
      const newLines = [...prevLinesRef.current];
      newLines[targetLineIndex] = currentLineContent;
      newLines.splice(targetLineIndex + 1, 0, content);
      
      setLines(newLines);
      prevLinesRef.current = newLines;
      notifyChange(newLines);
      
      // Focus logic
      setTimeout(() => {
        const focusIndex = targetLineIndex + 1;
        //console.log('Setting focus to line after insertion:', focusIndex);
        setActiveLineIndex(focusIndex);
        
        if (lineRefs.current[focusIndex]) {
          const lineElToFocus = lineRefs.current[focusIndex];
          const editableElToFocus = lineElToFocus.querySelector('[contenteditable="true"]') || lineElToFocus;
          editableElToFocus.focus();
          
          try {
            const newSel = window.getSelection();
            const newRg = document.createRange();
            if (editableElToFocus.firstChild) newRg.setStart(editableElToFocus.firstChild, 0);
            else newRg.setStart(editableElToFocus, 0);
            newRg.collapse(true);
            newSel.removeAllRanges();
            newSel.addRange(newRg);
          } catch (e) { console.warn("Error focusing after insertion:", e); }
        }
      }, 10);
      
      //console.log('=== insertAtCursorPosition SUCCESS ===');
      return true;
    }
    
    //console.log('❌ Could not determine valid line index for insertion.');
    return false;
  }, [notifyChange, setActiveLineIndex, activeLineIndex]);
  
  // Add method for inserting a Mermaid diagram
  const insertMermaidDiagram = useCallback((mermaidContent) => {
    //console.log('insertMermaidDiagram called');
    const mermaidBlock = createMermaidMarkdown(mermaidContent);
    
    // Try the direct cursor insertion first
    const success = insertAtCursorPosition(mermaidBlock);
    
    if (!success) {
      //console.log('Falling back to standard insertion for Mermaid block');
      // If direct insertion failed, fall back to the previous approach
      const fallbackLine = getCurrentLineFromCursor();
      
      if (fallbackLine !== null) {
        handleInsertMarkdown(mermaidBlock, fallbackLine);
      } else if (activeLineIndex !== null) {
        handleInsertMarkdown(mermaidBlock, activeLineIndex);
      } else if (prevLinesRef.current.length > 0) {
        handleInsertMarkdown(mermaidBlock, prevLinesRef.current.length - 1);
      } else {
        const newLines = [mermaidBlock];
        setLines(newLines);
        prevLinesRef.current = newLines;
        notifyChange(newLines);
      }
    }
  }, [insertAtCursorPosition, getCurrentLineFromCursor, handleInsertMarkdown, activeLineIndex, notifyChange]);
  
  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    insertMarkdown: (markdown) => {
      //console.log('insertMarkdown called');
      
      // Try the direct cursor insertion first
      const success = insertAtCursorPosition(markdown);
      
      if (!success) {
        //console.log('Falling back to standard insertion for markdown');
        // If direct insertion failed, fall back to the previous approach
        const fallbackLine = getCurrentLineFromCursor();
        
        if (fallbackLine !== null) {
          handleInsertMarkdown(markdown, fallbackLine);
        } else if (activeLineIndex !== null) {
          handleInsertMarkdown(markdown, activeLineIndex);
        } else if (prevLinesRef.current.length > 0) {
          handleInsertMarkdown(markdown, prevLinesRef.current.length - 1);
        } else {
          const newLines = [markdown];
          setLines(newLines);
          prevLinesRef.current = newLines;
          notifyChange(newLines);
        }
      }
    },
    
    // Capture and return the current clicked line index 
    // This should be called when starting the Excalidraw process
    getLastClickedLineIndex: () => {
      //console.log('getLastClickedLineIndex called, activeLineIndex is:', activeLineIndex);
      return activeLineIndex;
    },
    
    // Add new method to insert an Excalidraw block with a reference ID
    insertExcalidrawBlock: (refId, targetLineIndex = null) => {
      //console.log('insertExcalidrawBlock called with refId:', refId, 'targetLineIndex:', targetLineIndex);
      
      // Store the current activeLineIndex before any operations
      const savedLineIndex = targetLineIndex !== null ? targetLineIndex : activeLineIndex;
      //console.log('Using line index for insertion:', savedLineIndex, '(activeLineIndex was:', activeLineIndex, ')');
      
      const excalidrawBlock = createExcalidrawMarkdown(refId);
      
      // Use the specified targetLineIndex (if provided) or try to use activeLineIndex
      if (savedLineIndex !== null && savedLineIndex >= 0 && savedLineIndex < prevLinesRef.current.length) {
        //console.log('Inserting at specific line index:', savedLineIndex);
        
        // Direct insertion at the specified line index, bypassing cursor position logic
        const currentLineContent = prevLinesRef.current[savedLineIndex] || '';
        const newLines = [...prevLinesRef.current];
        newLines[savedLineIndex] = currentLineContent;
        newLines.splice(savedLineIndex + 1, 0, excalidrawBlock);
        
        setLines(newLines);
        prevLinesRef.current = newLines;
        notifyChange(newLines);
        
        // Focus the new line
        setTimeout(() => {
          const focusIndex = savedLineIndex + 1;
          //console.log('Setting focus to line after insertion:', focusIndex);
          setActiveLineIndex(focusIndex);
          
          if (lineRefs.current[focusIndex]) {
            const lineEl = lineRefs.current[focusIndex];
            const editableDiv = lineEl.querySelector('[contenteditable="true"]') || lineEl;
            editableDiv.focus();
            
            try {
              const selection = window.getSelection();
              const range = document.createRange();
              if (editableDiv.firstChild) {
                range.setStart(editableDiv.firstChild, 0);
              } else {
                range.setStart(editableDiv, 0);
              }
              range.collapse(true);
              selection.removeAllRanges();
              selection.addRange(range);
            } catch (e) {
              console.warn('Error positioning cursor after excalidraw insertion:', e);
            }
          }
        }, 10);
        
        return;
      }
      
      // Fallback to the normal insertion logic if no valid targetLineIndex
      //console.log('No valid targetLineIndex, falling back to normal insertion logic');
      
      // Try the direct cursor insertion first
      const success = insertAtCursorPosition(excalidrawBlock);
      
      if (!success) {
        //console.log('Falling back to standard insertion for Excalidraw block');
        // If direct insertion failed, fall back to the previous approach
        const fallbackLine = getCurrentLineFromCursor();
        
        if (fallbackLine !== null) {
          handleInsertMarkdown(excalidrawBlock, fallbackLine);
        } else if (activeLineIndex !== null) {
          handleInsertMarkdown(excalidrawBlock, activeLineIndex);
        } else if (prevLinesRef.current.length > 0) {
          handleInsertMarkdown(excalidrawBlock, prevLinesRef.current.length - 1);
        } else {
          const newLines = [excalidrawBlock];
          setLines(newLines);
          prevLinesRef.current = newLines;
          notifyChange(newLines);
        }
      }
    },
    
    // Add method for inserting Mermaid diagrams
    insertMermaidDiagram,
    
    // Add new method to preserve activeLineIndex during async operations
    preserveActiveLineIndex: () => {
      // Store current active line index 
      return activeLineIndex; 
    },
    
    // Add new method to restore activeLineIndex before insertion
    restoreActiveLineIndex: (savedIndex) => {
      if (savedIndex !== null && savedIndex >= 0) {
        //console.log(`Restoring previously saved activeLineIndex: ${savedIndex}`);
        setActiveLineIndex(savedIndex);
      }
    },
    
    focus: () => {
      // If there's no active line index set, use the first line
      if (activeLineIndex === null || activeLineIndex < 0) {
        setActiveLineIndex(0);
        if (lineRefs.current[0]) {
          handleLineClick(0);
        }
      } else {
        // If already has an active line, just focus on it
        if (lineRefs.current[activeLineIndex]) {
          handleLineClick(activeLineIndex);
        } else if (lineRefs.current[0]) {
          // Fallback to first line if active line doesn't exist
          handleLineClick(0);
        }
      }
    }
  }), [activeLineIndex, handleInsertMarkdown, notifyChange, getCurrentLineFromCursor, insertAtCursorPosition, insertMermaidDiagram]);
  
  const handleLineClick = useCallback((index) => {
    //console.log(`handleLineClick called for line ${index}, current activeLineIndex: ${activeLineIndex}`);
    commitActiveLine(); // Commit previous line before switching
    setActiveLineIndex(index);
    //console.log(`handleLineClick setting activeLineIndex to ${index}`);
    setActiveBlock(null); // Clicking a normal line exits block editing mode
    setEditingBlock(false);
    
    // Focus and position cursor at the end
    setTimeout(() => {
      if (lineRefs.current[index]) {
        const lineEl = lineRefs.current[index]; // This is LineContainer
        const editableDiv = lineEl.querySelector('[contenteditable="true"]') || lineEl;
        
        editableDiv.focus();
        
        const selection = window.getSelection();
        const range = document.createRange();
        
        // Place cursor at the end
        try {
            range.selectNodeContents(editableDiv);
            range.collapse(false); // Collapse to the end
            selection.removeAllRanges();
            selection.addRange(range);
            //console.log(`Cursor positioned at end of line ${index}, activeLineIndex now: ${activeLineIndex}`);
        } catch (e) {
            console.warn("Error positioning cursor at end of line on click:", e);
        }
      }
    }, 0);
  }, [commitActiveLine, activeLineIndex]);
  
  const handleBlockClick = useCallback((block) => {
    commitActiveLine(); // Commit previous line/block before switching
    setEditingBlock(true);
    setActiveLineIndex(block.startIndex);
    setActiveBlock(block);
    
    // Focus on the first line of the block
    setTimeout(() => {
      if (lineRefs.current[block.startIndex]) {
        lineRefs.current[block.startIndex].focus();
        // Optionally, position cursor at end of first block line
        const selection = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(lineRefs.current[block.startIndex]);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }, 0);
  }, [commitActiveLine]);
  
  const handleBlockCommit = useCallback(() => {
    if (activeBlock) {
      commitActiveLine(); // Commit the last edited line within the block
      
      if (blockEditTimerRef.current) {
        clearTimeout(blockEditTimerRef.current);
        blockEditTimerRef.current = null;
      }
      
      // Set editing block to false after a short delay
      blockEditTimerRef.current = setTimeout(() => {
        setEditingBlock(false);
        setActiveBlock(null); // Clear active block reference
        // Don't clear activeLineIndex here, let blur handle it if needed
      }, 50); // Shorter delay for responsiveness
    }
  }, [activeBlock, commitActiveLine]);
  
  const handleLineBlur = useCallback(() => {
    // Use a timeout to allow focus to shift between lines without committing prematurely
    setTimeout(() => {
      const activeEl = document.activeElement;
      const isEditorFocused = containerRef.current && containerRef.current.contains(activeEl);

      if (!isEditorFocused) {
          // Focus left the editor entirely
          commitActiveLine();
          if (activeBlock) {
            handleBlockCommit(); // Ensure block state is cleared
          }
          setActiveLineIndex(null);
          setActiveBlock(null);
          setEditingBlock(false);
      } else {
          // Focus moved to another element within the editor, check if it's a different line
          const currentFocusIndex = getLineIndexFromNode(activeEl, lineRefs);
          if (currentFocusIndex !== activeLineIndex) {
              // Focus moved to a different line/block
              commitActiveLine();
              if (activeBlock && (currentFocusIndex === null || currentFocusIndex < activeBlock.startIndex || currentFocusIndex > activeBlock.endIndex)) {
                  // Moved out of the active block
                  handleBlockCommit();
              }
          }
      }
    }, 0); // Small delay
  }, [activeLineIndex, commitActiveLine, activeBlock, handleBlockCommit]);
  
  const handleKeyDown = useCallback((e, index) => {
    skipNextBlurCommit.current = false; // Reset flag
    
    // Handle Delete and Backspace for multi-line selections
    if (e.key === 'Delete' || e.key === 'Backspace') {
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0 && !selection.isCollapsed) {
            const range = selection.getRangeAt(0);
            const startIdx = getLineIndexFromNode(range.startContainer, lineRefs);
            const endIdx = getLineIndexFromNode(range.endContainer, lineRefs);

            if (startIdx !== null && endIdx !== null && startIdx !== endIdx) {
                // Multi-line deletion logic (slightly simplified)
                e.preventDefault();
                
                // Get content before and after selection
                const startLineNode = lineRefs.current[startIdx];
                const endLineNode = lineRefs.current[endIdx];
                
                // Create temporary ranges to extract text content accurately
                const startRange = document.createRange();
                startRange.setStart(startLineNode, 0);
                startRange.setEnd(range.startContainer, range.startOffset);
                const beforeText = startRange.toString();

                const endRange = document.createRange();
                endRange.setStart(range.endContainer, range.endOffset);
                endRange.setEnd(endLineNode, endLineNode.childNodes.length); // Get content from selection end to line end
                const afterText = endRange.toString();

                const newLines = [
                    ...prevLinesRef.current.slice(0, startIdx),
                    beforeText + afterText,
                    ...prevLinesRef.current.slice(endIdx + 1)
                ];

                setLines(newLines);
                prevLinesRef.current = newLines;
                notifyChange(newLines);

                setTimeout(() => {
                    setActiveLineIndex(startIdx);
                    if (lineRefs.current[startIdx]) {
                        lineRefs.current[startIdx].focus();
                        const newRange = document.createRange();
                        const targetNode = lineRefs.current[startIdx].firstChild || lineRefs.current[startIdx];
                        const newOffset = Math.min(beforeText.length, targetNode.textContent?.length || 0);
                        
                        try {
                          if (targetNode.nodeType === 3) {
                            newRange.setStart(targetNode, newOffset);
                          } else { // Element node or empty
                            newRange.setStart(targetNode, Math.min(newOffset, targetNode.childNodes.length));
                          }
                          newRange.collapse(true);
                          selection.removeAllRanges();
                          selection.addRange(newRange);
                        } catch (cursorErr) { /* ... error handling ... */ }
                    }
                }, 0);
                return;
            }
        }
        // Single line backspace at start of line (excluding index 0)
        else if (e.key === 'Backspace' && index > 0) {
            const selection = window.getSelection();
            if (selection && selection.isCollapsed && selection.anchorOffset === 0) {
                const lineIsEmpty = lineRefs.current[index]?.innerText === ''; // Check current DOM content
                if(lineIsEmpty) {
                    e.preventDefault();
                    commitActiveLine(); // Commit any pending changes first
                    
                    const prevLineContent = prevLinesRef.current[index - 1];
                    const prevLineLength = prevLineContent.length;
                    const newLines = [...prevLinesRef.current];
                    newLines.splice(index, 1); // Remove current line
                    
                    setLines(newLines);
                    prevLinesRef.current = newLines;
                    notifyChange(newLines);

                    setTimeout(() => {
                        setActiveLineIndex(index - 1);
                        if (lineRefs.current[index - 1]) {
                            const prevLineNode = lineRefs.current[index - 1];
                            prevLineNode.focus();
                            // Position cursor at end of previous line
                            const range = document.createRange();
                            range.selectNodeContents(prevLineNode);
                            range.collapse(false);
                            selection.removeAllRanges();
                            selection.addRange(range);
                        }
                    }, 10);
                    return;
                }
            }
        }
    }

    // Handle Enter key
    if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        const committed = commitActiveLine(); // Commit current line before splitting
        
        const selection = window.getSelection();
        const range = selection.getRangeAt(0);
        const container = range.startContainer;
        const cursorPosition = range.startOffset;

        // Calculate the true offset relative to the line's start
        let trueOffset = 0;
        try {
          const currentLineEl = lineRefs.current[index];
          if (currentLineEl && (container === currentLineEl || currentLineEl.contains(container))) {
              const tempRange = document.createRange();
              tempRange.setStart(currentLineEl, 0);
              tempRange.setEnd(container, cursorPosition);
              trueOffset = tempRange.toString().length;
          } else if (currentLineEl) {
             // If cursor is somehow outside, assume it's at the end based on committed state
             trueOffset = prevLinesRef.current[index]?.length || 0; 
          }
        } catch (err) {
            console.warn("Could not accurately determine Enter split offset:", err);
            // Fallback to using the committed state length
            trueOffset = prevLinesRef.current[index]?.length || 0; 
        }

        const currentLineContent = prevLinesRef.current[index];
        const beforeCursor = currentLineContent.substring(0, trueOffset);
        const afterCursor = currentLineContent.substring(trueOffset);

        const newLines = [...prevLinesRef.current];
        newLines[index] = beforeCursor;
        newLines.splice(index + 1, 0, afterCursor);

        // Set state first
        setLines(newLines);
        prevLinesRef.current = newLines;
        notifyChange(newLines);
        setActiveLineIndex(index + 1); // Update active index immediately

        // Focus and position cursor *after* state update settles
        setTimeout(() => {
            const newLineEl = lineRefs.current[index + 1];
            if (newLineEl) {
                // Ensure focus lands on the new element
                newLineEl.focus(); 
                
                // Position cursor at the start of the new line
                try {
                    const selection = window.getSelection();
                    const newRange = document.createRange();
                    
                    // Check if the new line is empty or has content
                    if (newLineEl.firstChild) {
                        newRange.setStart(newLineEl.firstChild, 0);
                    } else {
                        // If empty, set start on the element itself
                        newRange.setStart(newLineEl, 0);
                    }
                    
                    newRange.collapse(true); // Collapse to the start
                    selection.removeAllRanges();
                    selection.addRange(newRange);
                } catch (err) {
                    console.error("Error setting cursor after Enter:", err);
                    // Fallback focus if range fails
                    if (document.activeElement !== newLineEl) {
                         newLineEl.focus();
                    }
                }
            } else {
                console.warn("Could not find new line element ref after Enter");
            }
        }, 0); // Use timeout 0 to queue after render
        return;
    }
    
    // Handle Tab key
    if (e.key === 'Tab') {
        e.preventDefault();
        // Manually insert tab character using execCommand (more reliable for contentEditable)
        document.execCommand('insertText', false, '\t');
        // No need to update state here, commit happens on blur/other keys
        return;
    }
    
    // Handle Arrow Keys (Up/Down)
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault(); // Prevent default scroll absolutely first
        
        const targetIndex = e.key === 'ArrowUp' ? index - 1 : index + 1;
        const isMovingUp = e.key === 'ArrowUp';
        const isMovingDown = e.key === 'ArrowDown';

        if ((isMovingUp && index > 0) || (isMovingDown && index < prevLinesRef.current.length - 1)) {
            const committed = commitActiveLine(); // Commit before navigating
            
            // Check if moving out of an active block
            if (activeBlock) {
                const isFirstLineOfBlock = index === activeBlock.startIndex;
                const isLastLineOfBlock = index === activeBlock.endIndex;
                if ((isMovingUp && isFirstLineOfBlock) || (isMovingDown && isLastLineOfBlock)) {
                    handleBlockCommit();
                }
            }

            // Save current horizontal position *before* state change
            let desiredColumn = 0;
            try {
                const selection = window.getSelection();
                if (selection && selection.rangeCount > 0) {
                    const range = selection.getRangeAt(0);
                    // Create a temporary range to measure offset from the line start
                    const measureRange = document.createRange();
                    const currentLineEl = lineRefs.current[index];
                    if (currentLineEl && currentLineEl.contains(range.startContainer)) {
                        measureRange.setStart(currentLineEl, 0);
                        measureRange.setEnd(range.startContainer, range.startOffset);
                        desiredColumn = measureRange.toString().length;
                    } else {
                        // Fallback if selection is somehow outside the current line
                        desiredColumn = selection.anchorOffset; 
                    }
                }
            } catch (err) {
                console.warn("Could not accurately determine cursor column:", err);
            }
            
            // Update active line *after* committing and saving column
            setActiveLineIndex(targetIndex);

            // Focus and position cursor *after* state update settles
            setTimeout(() => {
                const targetLineEl = lineRefs.current[targetIndex];
                if (targetLineEl) {
                    targetLineEl.focus();
                    
                    // Restore horizontal position (best effort)
                    try {
                        const selection = window.getSelection();
                        const newRange = document.createRange();
                        
                        // Find the best node and offset within the target line
                        let currentOffset = 0;
                        let foundNode = null;
                        let finalOffset = 0;
                        
                        function findPosition(node) {
                           if (node.nodeType === 3) { // Text node
                                const nodeLength = node.textContent.length;
                                if (desiredColumn <= currentOffset + nodeLength) {
                                    foundNode = node;
                                    finalOffset = desiredColumn - currentOffset;
                                    return true; // Position found
                                }
                                currentOffset += nodeLength;
                           } else if (node.nodeType === 1) { // Element node
                               for (let i = 0; i < node.childNodes.length; i++) {
                                   if (findPosition(node.childNodes[i])) {
                                       return true; // Position found in child
                                   }
                               }
                           }
                           return false; // Position not found in this subtree
                        }
                        
                        if (!findPosition(targetLineEl)) {
                           // Fallback: if position wasn't found (e.g., desiredColumn > line length)
                           // place cursor at the end of the line.
                           newRange.selectNodeContents(targetLineEl);
                           newRange.collapse(false); // false = collapse to end
                        } else {
                           newRange.setStart(foundNode, finalOffset);
                           newRange.collapse(true);
                        }
                        
                        selection.removeAllRanges();
                        selection.addRange(newRange);
                    } catch (err) {
                        console.error("Error restoring cursor column:", err);
                         // Fallback: Ensure focus is still set even if cursor positioning fails
                        if (document.activeElement !== targetLineEl) {
                            targetLineEl.focus();
                        }
                    }
                }
            }, 0); 
        }
        return; // Handled arrow key
    }
    
    // Handle Save (Ctrl+S / Cmd+S)
    if (e.key === 's' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      commitActiveLine();
      if (activeBlock) {
        handleBlockCommit();
      }
      if (onSave) {
        onSave(prevLinesRef.current.join('\n'));
      }
      return;
    }

    // Handle Escape Key
    if (e.key === 'Escape') {
        if (activeBlock) {
            e.preventDefault();
            commitActiveLine();
            handleBlockCommit();
            // Maybe move focus out or to the start of the block?
            // For now, just commit and exit block mode.
        }
        // We might want blur logic here if not in a block
        return;
    }

  }, [notifyChange, onSave, commitActiveLine, activeBlock, handleBlockCommit]);
  
  // Composition handling
  const handleCompositionStart = useCallback(() => setIsComposing(true), []);
  const handleCompositionEnd = useCallback(() => {
      setIsComposing(false);
      // Defer commit slightly after composition ends
      setTimeout(commitActiveLine, 0);
  }, [commitActiveLine]);
  
  // Ensure lineRefs array has correct size
  useEffect(() => {
    if (lineRefs.current.length !== lines.length) {
      // Create new array with appropriate length, preserving existing refs
      lineRefs.current = Array(lines.length).fill().map((_, i) => 
        i < lineRefs.current.length ? lineRefs.current[i] : null
      );
    }
  }, [lines.length]);
  
  // Get stable ID for blocks to prevent reconciliation issues
  const getBlockId = useCallback((startIndex) => {
    if (!blockIdMapRef.current.has(startIndex)) {
      blockIdMapRef.current.set(startIndex, `block-${startIndex}-${Date.now()}`);
    }
    return blockIdMapRef.current.get(startIndex);
  }, []);
  
  // Handle committing content for blocks
  const commitBlockContent = useCallback((blockContent, startIndex, endIndex) => {
    if (startIndex !== null && endIndex !== null) {
      const newLines = [...prevLinesRef.current];
      
      // Split the content by lines
      const contentLines = blockContent.split('\n');
      
      // Replace the existing lines with the new content
      newLines.splice(startIndex, endIndex - startIndex + 1, ...contentLines);
      
      setLines(newLines);
      prevLinesRef.current = newLines;
      notifyChange(newLines);
      return true;
    }
    return false;
  }, [notifyChange]);
  
  return (
    <EditorContainer ref={containerRef}>
      {processedContent.map((item) => {
        if (item.type === 'line') {
          const index = item.index;
          const isActive = activeLineIndex === index;
          
          return (
            <LineBlock 
              key={`line-${index}`}
              isActive={isActive}
              isEditable={isActive}
              ref={el => { if (el) lineRefs.current[index] = el; }}
              content={item.content}
              onClick={() => {
                //console.log(`LineBlock onClick explicit handler for line ${index}`);
                handleLineClick(index);
              }}
              onMouseDown={(e) => {
                // Redundant handler to ensure line activation even if click handler fails
                //console.log(`LineBlock onMouseDown for line ${index}`);
                // Don't call handleLineClick directly here to avoid double activation
                // Just log for debugging - we'll see if this gets called but onClick doesn't
              }}
              onBlur={handleLineBlur}
              onKeyDown={e => handleKeyDown(e, index)}
              onCompositionStart={handleCompositionStart}
              onCompositionEnd={handleCompositionEnd}
              onContentChange={(content) => handleLineContentChange(index, content)}
              commitContent={(content) => commitActiveLine(content)}
              lineIndex={index} // Add explicit line index as prop for debugging
            >
              {!isActive && isListItemBlock(item.content) ? (
                <ListItemBlock
                  isActive={false}
                  content={item.content}
                  onClick={() => handleLineClick(index)}
                />
              ) : !isActive && (
                <ListContainer>
                  <ReactMarkdown components={markdownRenderers}>
                    {processMarkdownIndentation(String(item.content) || ' ')}
                  </ReactMarkdown>
                </ListContainer>
              )}
            </LineBlock>
          );
        } else if (item.type === 'block') {
          const isCurrentlyEditingThisBlock = activeBlock && item.startIndex === activeBlock.startIndex;
          
          // Handle list blocks
          if (isListItemBlock(item.content)) {
            return (
              <ListItemBlock 
                key={`list-block-${item.startIndex}`}
                isActive={isCurrentlyEditingThisBlock}
                content={item.content.join('\n')}
                onClick={() => handleBlockClick(item)}
                onContentChange={(content) => {
                  // Store the content temporarily, no immediate update
                  activeLineContentRef.current = content;
                }}
                commitContent={(content) => {
                  return commitBlockContent(content, item.startIndex, item.endIndex);
                }}
              />
            );
          }
          
          // Handle mermaid blocks
          if (isMermaidBlock(item.content)) {
            return (
              <MermaidBlock 
                key={`mermaid-block-${item.startIndex}`}
                isActive={isCurrentlyEditingThisBlock}
                content={item.content.join('\n')}
                onClick={() => handleBlockClick(item)}
                onContentChange={(content) => {
                  // Store the content temporarily, no immediate update
                  activeLineContentRef.current = content;
                }}
                commitContent={(content) => {
                  // Use the content as-is, which should already include fences
                  return commitBlockContent(content, item.startIndex, item.endIndex);
                }}
              />
            );
          }
          
          // Handle excalidraw blocks specially
          if (isExcalidrawBlock(item.content)) {
            return (
              <ExcalidrawBlock 
                key={`excalidraw-block-${item.startIndex}`}
                isActive={isCurrentlyEditingThisBlock}
                content={item.content.slice(1, item.content.length - 1)} // Remove the code fence markers
                onClick={() => handleBlockClick(item)}
                onContentChange={(content) => {
                  // Store the content temporarily, no immediate update
                  activeLineContentRef.current = content;
                }}
                commitContent={(content) => {
                  // Add back the code fence markers
                  const fullContent = [
                    '```excalidraw',
                    ...content.split('\n'),
                    '```'
                  ].join('\n');
                  return commitBlockContent(fullContent, item.startIndex, item.endIndex);
                }}
              />
            );
          }
          
          // Handle table blocks specially
          if (isTableBlock(item.content)) {
            return (
              <TableBlock 
                key={`table-block-${item.startIndex}`}
                isActive={isCurrentlyEditingThisBlock}
                content={item.content.slice(1, item.content.length - 1)} // Remove the code fence markers
                onClick={() => handleBlockClick(item)}
                onContentChange={(content) => {
                  // Store the content temporarily, no immediate update
                  activeLineContentRef.current = content;
                }}
                commitContent={(content) => {
                  // Add back the code fence markers
                  const fullContent = [
                    '```table',
                    ...content.split('\n'),
                    '```'
                  ].join('\n');
                  return commitBlockContent(fullContent, item.startIndex, item.endIndex);
                }}
              />
            );
          }
          
          return (
            <React.Fragment key={getBlockId(item.startIndex)}>
              {isCurrentlyEditingThisBlock ? (
                // Render editable lines for the active block
                item.content.map((line, i) => {
                  const index = item.startIndex + i;
                  const isActiveLineInBlock = activeLineIndex === index;
                  return (
                    <LineBlock
                      key={`block-line-${index}`}
                      isActive={isActiveLineInBlock}
                      isEditable={true}
                      ref={el => { if (el) lineRefs.current[index] = el; }}
                      content={line}
                      onBlur={handleLineBlur}
                      onKeyDown={e => handleKeyDown(e, index)}
                      onCompositionStart={handleCompositionStart}
                      onCompositionEnd={handleCompositionEnd}
                      onContentChange={(content) => handleLineContentChange(index, content)}
                      commitContent={(content) => commitActiveLine(content)}
                    />
                  );
                })
              ) : (
                // Render the block as a single unit (special or markdown)
                <BlockContainer 
                  onClick={() => handleBlockClick(item)}
                >
                  {isSpecialBlock(item.content[0]) ? (
                    <RenderUnknownBlock 
                      key={`special-${item.startIndex}`}
                      content={item.content} 
                    />
                  ) : isListItemBlock(item.content) ? (
                    <ListItemBlock
                      onClick={() => handleBlockClick(item)}
                      isActive={false}
                      content={item.content.join('\n')}
                    />
                  ) : (
                    <LineBlock
                      onClick={() => handleBlockClick(item)}
                    >
                      <ListContainer>
                        <ReactMarkdown components={markdownRenderers}>
                          {processMarkdownIndentation(item.content.join('\n'))}
                        </ReactMarkdown>
                      </ListContainer>
                    </LineBlock>
                  )}
                </BlockContainer>
              )}
            </React.Fragment>
          );
        }
        return null;
      })}
    </EditorContainer>
  );
});

ObsidianEditor.displayName = 'ObsidianEditor';

export default ObsidianEditor; 